<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import GridBoard from './GridBoard.vue';
import ModuleShop from './ModuleShop.vue';
import GestureCursor from './GestureCursor.vue';
import GestureContextHUD from './GestureContextHUD.vue';
import HomeScreen from '../HomeScreen.vue';
import MicrophoneSelector from './MicrophoneSelector.vue';
import { useWidgetManager } from '../../composables/useWidgetManager';
import { useWidgetResize } from '../../composables/useWidgetResize';
import { useEditMode } from '../../composables/useEditMode';
import { useModuleShop } from '../../composables/useModuleShop';
import { useClockWidgetMode } from '../../composables/useClockWidgetMode';
import { useActionDispatcher } from '../../composables/useActionDispatcher';
import { useHomeScreen } from '../../composables/useHomeScreen';
import { useHandTracking } from '../../composables/useHandTracking';
import { realtimeClient } from '../../services/realtime';
import { checkExternalApiHealth } from '../../services/systemHealth';
import type { UIActionType } from '../../types/interactions';

interface ModuleShopExposed {
  addCurrentWidgetToCell: (cellId: number) => void;
  nextModule: () => void;
  prevModule: () => void;
}

// View-State
const currentView = ref<'home' | 'grid'>('home');
function goToGrid(): void { currentView.value = 'grid'; }

// HomeScreen
const {
  cameras,
  currentIndex,
  frameUrl,
  error: cameraError,
  loading: cameraLoading,
  state: homeState,
  slideDirection,
  initializeCamera,
  navigateCamera,
  setHomeActive,
  stopStream,
} = useHomeScreen();

// Widget management
const { insertWidgetIntoCell, clearCell, moveWidgets, occupiedCells, widgetMap } = useWidgetManager();
const { getVisibleCells, resizeCell } = useWidgetResize();

const availableCells = computed(() => {
  const occupied = new Set(occupiedCells.value)
  return getVisibleCells().filter(id => !occupied.has(id))
});

const { isEditMode, setEditMode, setupKeyboardListener } = useEditMode();
const { isShopOpen, toggleShop, openShop, closeShop } = useModuleShop();
const { toggleClockMode } = useClockWidgetMode();
const { indexFingerCursor, pinchCursor } = useHandTracking();

const moduleShopRef = ref<ComponentPublicInstance<{}, ModuleShopExposed> | null>(null);
let unsubscribeRealtime: (() => void) | null = null;

const {
  focusedCellId,
  isDragging,
  dragSourceCell,
  deleteConfirmCell,
  handleRealtimeEvent,
  syncFocusedCell,
  dispatchAction,
} = useActionDispatcher({
  isShopOpen,
  openShop,
  closeShop,
  toggleShop,
  isEditMode,
  setEditMode,
  visibleCellIds: getVisibleCells,
  isCellAvailable: (cellId: number) => availableCells.value.includes(cellId),
  isCellOccupied: (cellId: number) => {
    const occupied = new Set(occupiedCells.value)
    return getVisibleCells().includes(cellId) && occupied.has(cellId)
  },
  resizeCell,
  moduleShopRef,
  currentView,
  navigateCamera,
  goToGrid,
  onWidgetMoved: (sourceCellId: number, targetCellId: number) => {
    moveWidgets({ sourceCellId, targetCellId });
    syncFocusedCell();
  },
  onWidgetDeleted: (cellId: number) => {
    clearCell(cellId);
    syncFocusedCell();
  },
});

const activeGestureCursor = computed(() =>
  isDragging.value ? (pinchCursor.value ?? indexFingerCursor.value) : indexFingerCursor.value
);

// Cursor → focusedCellId: always tracks the cell under the index finger in edit mode.
// This makes pinch gestures always act on whatever the cursor is pointing at.
watch(activeGestureCursor, (pos) => {
  if (!pos || !isEditMode.value) return;
  const x = (pos.x / 100) * window.innerWidth;
  const y = (pos.y / 100) * window.innerHeight;
  const el = document.elementFromPoint(x, y);
  const cellEl = el?.closest('[data-cell-id]') as HTMLElement | null;
  if (!cellEl) return;
  const cellId = Number(cellEl.dataset['cellId']);
  if (!isNaN(cellId) && cellId !== focusedCellId.value) {
    focusedCellId.value = cellId;
  }
});

// Drag widget name for cursor label (reads __name from the Vue SFC component)
const dragWidgetName = computed<string | null>(() => {
  if (!isDragging.value || dragSourceCell.value === null) return null;
  const component = widgetMap.value[dragSourceCell.value] as any;
  return component?.__name ?? 'Widget';
});

// HUD: is the focused cell empty?
const focusedCellIsEmpty = computed(() =>
  availableCells.value.includes(focusedCellId.value)
);

const handleAddWidget = ({ cellId, component }: { cellId: number; component: any }) => {
  insertWidgetIntoCell(cellId, component);
  syncFocusedCell();
};

const handleWidgetsMoved = ({ sourceCellId, targetCellId }: { sourceCellId: number; targetCellId: number }) => {
  moveWidgets({ sourceCellId, targetCellId });
  syncFocusedCell();
};

const handleDeleteWidget = (cellId: number) => {
  clearCell(cellId);
  syncFocusedCell();
};

const handleConfirmDelete = (cellId: number) => {
  deleteConfirmCell.value = null;
  clearCell(cellId);
  syncFocusedCell();
};

const handleCancelDelete = () => {
  deleteConfirmCell.value = null;
};

const handleRequestAdd = () => {
  moduleShopRef.value?.addCurrentWidgetToCell(focusedCellId.value);
  closeShop();
};

const handleHudAction = (action: UIActionType): void => {
  dispatchAction({
    action,
    timestamp: new Date().toISOString(),
    input_source: 'dev',
    raw_input: action,
    action_args: {},
    metadata: {},
  });
};

// Keyboard shop navigation (mouse/keyboard fallback)
const handleShopNavigation = (key: string) => {
  if (isShopOpen.value && moduleShopRef.value) {
    if (key === 'ArrowRight') moduleShopRef.value.nextModule();
    else if (key === 'ArrowLeft') moduleShopRef.value.prevModule();
  }
};

setupKeyboardListener({
  onShopToggle: toggleShop,
  onShopNavigate: handleShopNavigation,
  onClockToggle: toggleClockMode,
});

watch(availableCells, () => { syncFocusedCell(); });
watch(currentView, (view, previousView) => {
  setHomeActive(view === 'home');
  if (view === 'home' && previousView !== 'home') {
    void initializeCamera();
  }
}, { immediate: true });

onMounted(() => {
  void checkExternalApiHealth().catch((error) => {
    console.warn('External API health check failed', error);
  });
  syncFocusedCell();
  unsubscribeRealtime = realtimeClient.subscribe((event) => {
    handleRealtimeEvent(event);
  });
});

onBeforeUnmount(() => {
  setHomeActive(false);
  unsubscribeRealtime?.();
  unsubscribeRealtime = null;
  stopStream();
});
</script>

<template>
  <div class="app-root">

    <!-- Finger cursor overlay (edit mode only) -->
    <GestureCursor
      v-if="isEditMode || isShopOpen"
      :cursor="activeGestureCursor"
      :is-dragging="isDragging"
      :drag-widget-name="dragWidgetName"
    />

    <!-- Gesture context HUD -->
    <GestureContextHUD
      :is-edit-mode="isEditMode"
      :is-shop-open="isShopOpen"
      :is-dragging="isDragging"
      :delete-confirm-pending="deleteConfirmCell !== null"
      :focused-cell-is-empty="focusedCellIsEmpty"
      @action-clicked="handleHudAction"
    />

    <!-- HomeScreen (Kamera-Startseite) -->
    <Transition name="view-to-grid">
      <HomeScreen
        v-if="currentView === 'home'"
        :cameras="cameras"
        :current-index="currentIndex"
        :frame-url="frameUrl"
        :error="cameraError"
        :loading="cameraLoading"
        :state="homeState"
        :slide-direction="slideDirection"
        @goto-grid="goToGrid"
        @navigate-camera="navigateCamera"
      />
    </Transition>

    <!-- Grid-Ansicht -->
    <Transition name="view-from-right">
      <div v-if="currentView === 'grid'" class="grid-view">

        <!-- Module Shop Popup -->
        <Transition name="shop-rise">
          <div v-if="isShopOpen" class="shop-overlay" @click.self="closeShop">
            <div class="shop-modal">
              <button class="close-btn" @click="closeShop" aria-label="Shop schließen">×</button>
              <ModuleShop
                ref="moduleShopRef"
                :gesture-cursor="indexFingerCursor"
                @addWidget="handleAddWidget"
                @requestAdd="handleRequestAdd"
              />
            </div>
          </div>
        </Transition>

        <GridBoard
          :is-edit-mode="isEditMode"
          :focused-cell-id="focusedCellId"
          :is-dragging="isDragging"
          :drag-source-cell="dragSourceCell"
          :delete-confirm-cell="deleteConfirmCell"
          :gesture-cursor="indexFingerCursor"
          @widgets-moved="handleWidgetsMoved"
          @delete-widget="handleDeleteWidget"
          @confirm-delete="handleConfirmDelete"
          @cancel-delete="handleCancelDelete"
        />

        <!-- Microphone selection (bottom-left corner) -->
        <div class="mic-corner">
          <MicrophoneSelector />
        </div>
      </div>
    </Transition>

  </div>
</template>

<style scoped>
.app-root {
  position: fixed;
  inset: 0;
  overflow: hidden;
}

.grid-view {
  position: absolute;
  inset: 0;
}

.mic-corner {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 50;
}

/* View-Transition: HomeScreen → Grid */
.view-to-grid-leave-active,
.view-from-right-enter-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.view-to-grid-leave-to {
  transform: translateX(-100%);
}

.view-from-right-enter-from {
  transform: translateX(100%);
}

/* Shop modal */
.shop-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.shop-modal {
  position: relative;
  background: #111111;
  border-radius: 16px;
  padding: 20px;
  width: 92vw;
  max-width: 680px;
  height: 72vh;
  max-height: 560px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.75);
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.55);
  font-size: 22px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 180ms ease, color 180ms ease;
  z-index: 10;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
}

/* Shop entrance animation */
.shop-rise-enter-active,
.shop-rise-leave-active {
  transition: opacity 220ms ease;
}

.shop-rise-enter-active .shop-modal,
.shop-rise-leave-active .shop-modal {
  transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease;
}

.shop-rise-enter-from {
  opacity: 0;
}

.shop-rise-enter-from .shop-modal {
  transform: translateY(24px);
  opacity: 0;
}

.shop-rise-leave-to {
  opacity: 0;
}

.shop-rise-leave-to .shop-modal {
  transform: translateY(16px);
  opacity: 0;
}
</style>
